/**
 * Memoized timeline cells shared by day and week calendars. Each cell receives only its own
 * placements; unrelated progress text and activity updates do not rerender the entire grid.
 * Activity references stay stable across batched store mutations, while each block comparison
 * includes every render-bearing field so changed titles, state and placement remain visible.
 *
 * Zoom is cheap: cell heights come from the grid rows that the owning view sizes, so a zoom step
 * re-renders only cells that hold blocks, and a block re-renders only when its compact layout
 * flips ({@link isCompactAt}).
 * @module apps/web/src/components/calendar/CalendarTimeline
 */
import { memo, useCallback } from 'react';
import type { ScheduleBlock, TaskUI } from '@fluxability/types';
import { DroppableTimeSlot } from './DroppableTimeSlot';
import { ScheduledTaskBlock } from './ScheduledTaskBlock';
import { RecordedWorkSegment } from './RecordedWorkSegment';
import { activityIsDone } from '@/components/activities/ActivityDone';

/** Timeline placement and its already indexed activity; missing tasks represent calendar events. */
export interface TimelineEntry {
  /** Display-only block, including provisional and provider flags. */
  block: ScheduleBlock;
  /** Current canonical activity, never the provisional replacement document. */
  task?: TaskUI;
  /** Zero-based side-by-side lane when the block overlaps others that day; defaults to 0. */
  lane?: number;
  /** Lanes in the block's overlap cluster (`timelineLanes.ts`); defaults to 1, the full width. */
  lanes?: number;
}

/** Equality of two entries' lane placement. */
function sameLane(left: TimelineEntry, right: TimelineEntry): boolean {
  return (left.lane ?? 0) === (right.lane ?? 0) && (left.lanes ?? 1) === (right.lanes ?? 1);
}

/** Equality covers every ScheduleBlock property, including future added presentation fields. */
function sameBlock(left: ScheduleBlock, right: ScheduleBlock): boolean {
  if (left === right) return true;
  const keys = Object.keys(left) as Array<keyof ScheduleBlock>;
  return (
    keys.length === Object.keys(right).length && keys.every(key => Object.is(left[key], right[key]))
  );
}

/**
 * Whether `block` renders in its compact layout at `slotHeight` pixels per hour: under 32 px tall.
 * `ScheduledTaskBlock` uses `slotHeight` only for this decision, so two heights with the same
 * answer render the same block.
 */
export function isCompactAt(block: ScheduleBlock, slotHeight: number): boolean {
  const hours = (new Date(block.endTime).getTime() - new Date(block.startTime).getTime()) / 3600000;
  return hours * slotHeight < 32;
}

/** Memoized block boundary with a stable click callback bound to the placement identity. */
const TimelineBlock = memo(
  function TimelineBlock({
    entry,
    onSelect,
    slotHeight,
  }: {
    /** Placement and current task at this point in time. */
    entry: TimelineEntry;
    /** Pixel height of one hour for compact card presentation at the current zoom. */
    slotHeight: number;
    /** Stable owner callback resolving the appropriate editor or suggestion action. */
    onSelect: (block: ScheduleBlock) => void;
  }) {
    const onClick = useCallback(() => onSelect(entry.block), [onSelect, entry.block]);
    if (entry.block.sessionId) return <RecordedWorkSegment done={activityIsDone(entry.task)} block={entry.block} onClick={onClick} lane={entry.lane} lanes={entry.lanes} />;
    return (
      <ScheduledTaskBlock
        block={entry.block}
        task={entry.task}
        onClick={onClick}
        slotHeight={slotHeight}
        lane={entry.lane}
        lanes={entry.lanes}
      />
    );
  },
  (previous, next) =>
    previous.onSelect === next.onSelect &&
    (previous.slotHeight === next.slotHeight ||
      isCompactAt(next.entry.block, previous.slotHeight) ===
        isCompactAt(next.entry.block, next.slotHeight)) &&
    previous.entry.task === next.entry.task &&
    sameLane(previous.entry, next.entry) &&
    sameBlock(previous.entry.block, next.entry.block)
);

/** Geometry and data for one hour/day cell, independent of the rest of the schedule. */
interface TimelineCellProps {
  /** Stable dnd identifier unique within the active calendar. */
  id: string;
  /** Browser-local YYYY-MM-DD date, matching the visual column. */
  dateKey: string;
  /** Local hour from zero through twenty-three. */
  hour: number;
  /** Pixel height of one hour at current zoom; the cell's own height follows the owner's
   * grid row, so an empty cell does not re-render for a zoom step. */
  slotHeight: number;
  /** Local workday start used only for visual work-window boundaries. */
  workStart: number;
  /** Local workday end used only for visual work-window boundaries. */
  workEnd: number;
  /** Only placements starting inside this cell. */
  entries: TimelineEntry[];
  /** Owner callback; must stay stable across solve progress updates. */
  onSelect: (block: ScheduleBlock) => void;
}

/** Render one droppable cell only when its own placements, activity data or geometry changes. */
export const CalendarTimelineCell = memo(
  function CalendarTimelineCell(props: TimelineCellProps) {
    const { id, dateKey, hour, slotHeight, workStart, workEnd, entries, onSelect } = props;
    const hourStart = hour * 60;
    const hourEnd = hourStart + 60;
    const boundaries = [workStart, workEnd]
      .filter(value => value >= hourStart && value < hourEnd)
      .map(value => value - hourStart);
    return (
      <DroppableTimeSlot
        id={id}
        dateKey={dateKey}
        hour={hour}
        slotHeight={slotHeight}
        workingBoundaryMinutes={boundaries}
        isOutsideWorkingHours={hourEnd <= workStart || hourStart >= workEnd}
      >
        {entries.map(entry => (
          <TimelineBlock
            key={entry.block.id}
            entry={entry}
            onSelect={onSelect}
            slotHeight={slotHeight}
          />
        ))}
      </DroppableTimeSlot>
    );
  },
  (previous, next) =>
    previous.id === next.id &&
    previous.dateKey === next.dateKey &&
    previous.hour === next.hour &&
    (previous.slotHeight === next.slotHeight || next.entries.length === 0) &&
    previous.workStart === next.workStart &&
    previous.workEnd === next.workEnd &&
    previous.onSelect === next.onSelect &&
    previous.entries.length === next.entries.length &&
    previous.entries.every(
      (entry, index) =>
        entry.task === next.entries[index].task &&
        sameLane(entry, next.entries[index]) &&
        sameBlock(entry.block, next.entries[index].block)
    )
);

/** Shared empty cell input prevents allocating 168 fresh arrays during progress-only renders. */
export const EMPTY_TIMELINE_ENTRIES: TimelineEntry[] = [];
